import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getSeverityCounts as getSeverityCountService, getUpdatedTime as getUpdatedTimeService } from 'services/modules/soc/agent';

import ThreatHuntingAlerts from '../components/ThreatHuntingAlerts';
import MitreGraph from '../components/MitreGraph';
import ConfigurationAssessment from './ConfigurationAssessment';
import VulnerabilityDetectionGraph from './VulnerabilityDetectionGraph';
import Breadcrumbs from 'config/Breadcrumb';
import MalwareDetectionGraph from './MalwareDetectionGraph';

import { DatePicker, Space } from "antd";
import dayjs from "dayjs";
import timezone from 'dayjs/plugin/timezone'; 
import utc from 'dayjs/plugin/utc';
import { useNotification } from "config/Notification";

import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const { RangePicker } = DatePicker;


// Extend dayjs with the plugins
dayjs.extend(timezone);
dayjs.extend(utc);

const Dashboard = () => {
  // uselocation hook
  const location = useLocation();
  // React router
  const navigate = useNavigate();
  // query params
  const queryParams = new URLSearchParams(location.search);

  // Notification
  const {openNotification} = useNotification();

  // State variables
  const [data, setData] = useState({});
  const [currentPath, setCurrentPath] = useState(['Dashboard']);
  const [policyId, setPolicyId] = useState('');
  const [timeStamp, setTimeStamp] = useState(null);
  const [reportsFound, setReportsFound] = useState(null);
  const [startDateTime, setStartDateTime] = useState(new Date());
  const [endDateTime, setEndDateTime] = useState(new Date());
  const [maxValue, setMaxValue] = useState(null);
  const agentName = queryParams.get('agentname');
  const agentId = queryParams.get('agentid');

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData)

  // Function to send the policy ID as props from dashboard
  const handleData = (value) => {
    setPolicyId(value);
  };
  useEffect(() => {
    fetchTimeStamp()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId])

  // Fetches the counts of alerts of different severities within a given range
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (selectedCompanyContainer?.soc_env && endDateTime && startDateTime) {
          const data = await getSeverityCountService({ agentId: agentId, soc_env: selectedCompanyContainer?.soc_env, end: endDateTime, start: startDateTime });
          setData(data?.data?.aggregations?.severity_counts?.buckets);
        }
        setCurrentPath(['Dashboard', `${agentName}`]);
      } catch (error) { }
    };
    if (agentId) {
      fetchData();
    }
  }, [agentId, agentName, selectedCompanyContainer?.soc_env, endDateTime, startDateTime]);

  // Fetches the last updated time stamp for a particular agent to set the date range
  const fetchTimeStamp = async () => {
    const params = { agentId: agentId, soc_env: selectedCompanyContainer.soc_env }
    const response = await getUpdatedTimeService(params);
    if (response?.data?.[0]?.alert_timestamp) {
      setTimeStamp(response?.data?.[0]?.alert_timestamp);
      setReportsFound(true);
    } else {
      setTimeStamp(null);
      setReportsFound(false);
    }
  }

  // Converts the given date to desired format
  useEffect(() => {
    let start, end;

    if (timeStamp) {
      end = convertToIST(new Date(timeStamp).toISOString());
      const parsedDate = new Date(timeStamp);

      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid Date:", timeStamp);
      }
      setMaxValue(parsedDate);
    } else {
      end = new Date();
      end.setHours(0, 0, 0, 0);
    }

    start = convertToIST(new Date(end.getTime() - 24 * 60 * 60 * 1000).toISOString()); // Set start to 24 hours before end

    setStartDateTime(start);
    setEndDateTime(end);
  }, [timeStamp]);

 const convertToIST = (dateString) => {
      // Check if string includes an explicit offset (e.g., +, -)
   if (dateString.includes('+') || dateString.includes('-')) {
     // Parse as is and convert to IST, returning a Date object
     return dayjs(dateString).tz("Asia/Kolkata", true).toDate();  
   } else {
     // Assume it's UTC if it has 'Z', convert to IST and return a Date object
     return dayjs.utc(dateString).tz("Asia/Kolkata", true).toDate();  
   }
   };

  return (
    <div className='p-4 rating-value'>
      <Breadcrumbs paths={currentPath} />
      <Typography variant="h5">{agentName} Dashboard</Typography>
      <div className="row ps-4">
        <div
          className="  col-lg-4 col-md-6 col-sm-6 col-xs-12 mx-2"
          style={{ zIndex: "100" }}
        >
          <div className="w-full max-w-xl flex flex-row gap-4">
            <Space direction="vertical" size={12}>
              <RangePicker
                showTime
                value={[dayjs(startDateTime), dayjs(endDateTime)]}
                format="YYYY-MM-DD HH:mm:ss.SSS"
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    const start = convertToIST(dates[0].toDate().toISOString());
                    const end = convertToIST(dates[1].toDate().toISOString());


                    // Custom range validation
                    const diffInMs = end.getTime() - start.getTime();

                    if (diffInMs < 0) {
                      openNotification( "Start date must be before end date",    "warning"  );
                      return;
                    }
                    setStartDateTime(start);
                    setEndDateTime(end);
                  }
                }}
                disabledDate={(current) => {
                  // Disable future dates beyond maxValue
                  if (!maxValue) return false;
                  return current && current > dayjs(maxValue.toString());
                }}
              />
            </Space>
          </div>

        </div>

      </div>
      {
        !reportsFound && (
          <Dialog
            open={reportsFound === false}
            onClose={() => setReportsFound(true)}
            fullWidth
            maxWidth="xs"
          >
            <DialogContent>
              <div className="row d-flex justify-content-center align-items-center">
                <WarningAmberIcon fontSize="large" color='warning' />

                <h6 align="center" className='my-1'>Reports were not updated for {agentName}</h6>
                <span align="center">  (Contact SOC Team @<strong>info.rhymtech.com</strong>)</span>
              </div>

              <div className="row d-flex justify-content-center mt-2">
                <div className="col-lg-1 col-md-1 col-sm-3 col-xs-3 px-0">
                  <button
                    type="button"
                    class="btn btn-warning"
                    onClick={() => { navigate('/soc/view-agents') }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

        )
      }


      <Card sx={{ marginTop: '20px', marginLeft: '40px', marginRight: '40px' }}>
        <CardContent className="flex">
          {[
            { label: 'Critical Severity', key: 'critical_severity', color: 'red' },
            { label: 'High Severity', key: 'high_severity', color: '#fc659c' },
            { label: 'Medium Severity', key: 'medium_severity', color: 'orange' },
            { label: 'Low Severity', key: 'low_severity', color: '#4e4ece' },
          ].map((severity) => (
            <div
              key={severity.key}
              className="col-lg-3 col-md-3 col-sm-12 col-xs-12 d-flex justify-content-center align-items-center gap-2"
            >
              <div className='flex-column'>
                <div className="text-bold">{severity.label}</div>
                <span
                  style={{
                    color: severity.color,
                    cursor: data?.[severity.key]?.doc_count > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '1.75rem',
                  }}
                  onClick={() => {
                    if (data?.[severity.key]?.doc_count > 0) {
                      navigate(
                        `/soc/security-alerts/?agentname=${agentName}&agentid=${agentId}&severity=${severity.key.replace('_severity', '')}`, { state: { start: startDateTime, end: endDateTime } }
                      );
                    }
                  }}
                >
                  {data?.[severity.key]?.doc_count}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="row p-4">
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <ThreatHuntingAlerts agentId={agentId} agentName={agentName} endDate={endDateTime} startDate={startDateTime} />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <ConfigurationAssessment agentId={agentId} dataSend={handleData} agentName={agentName} policy={policyId} endDate={endDateTime} />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <MalwareDetectionGraph agentId={agentId} agentName={agentName} endDate={endDateTime} startDate={startDateTime} />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
          <VulnerabilityDetectionGraph agentId={agentId} agentName={agentName} endDate={endDateTime} startDate={startDateTime} />
        </div>
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <MitreGraph agentId={agentId} agentName={agentName} endDate={endDateTime} startDate={startDateTime} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
